import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../authContext';
import { getEmbeddedSignupByPhone, getBusinessMessages, getWhatsAppCallRecords, getCallAudio, updateCount } from '../../../api/login.service';
import { IoMdSearch, IoMdFunnel, IoMdRefresh, IoMdEye, IoMdChatbubbles, IoMdTime, IoMdCheckmarkCircle, IoMdCloseCircle, IoMdClock, IoMdArrowBack, IoMdArrowUp, IoMdArrowDown, IoMdPeople, IoMdPhonePortrait, IoMdCalendar, IoMdCall } from 'react-icons/io';
import LoadingComponent from '../../../components/LoadingComponent';
import { logger } from '../../../utils/logger';
import { cleanPhoneNumber } from '../../../utils/callUtils';

interface PhoneNumber {
  id: string;
  phoneNumber: string;
  phoneNumberId: string;
  wabaId: string;
  businessName?: string;
  status?: string;
  lastMessageTime?: string;
  unreadCount?: number;
}

interface Message {
  customerName?: string;
  customerNumber: string;
  timestamp: string;
  updatedAt?: string; // Use this for "Last Message" display
  unreadCount?: number; // Use this for "Unread" count display
  message?: string;
  type?: string;
}

interface Call {
  customerName?: string;
  customerNumber: string;
  timestamp: string;
  duration?: number;
  type?: 'incoming' | 'outgoing';
  status?: 'completed' | 'missed' | 'failed';
  callerId?: string;
  recording_url?: string | null;
}

interface PhoneNumbersResponse {
  data?: PhoneNumber[];
  paging?: {
    cursors?: { before?: string; after?: string };
    next?: string;
    previous?: string;
  };
}

type SortField = 'customerName' | 'customerNumber' | 'timestamp';
type SortDirection = 'asc' | 'desc';

interface FilterState {
  search: string;
  status: string;
  businessName: string;
  hasUnread: boolean;
  dateRange: string;
  startDate: string;
  endDate: string;
}

interface SortState {
  field: SortField;
  direction: SortDirection;
}

export default function PhoneNumbersList() {
  const navigate = useNavigate();
  
  // Get wabaId from session storage instead of URL params
  const wabaId = sessionStorage.getItem('wabaId');
  
  const { displayPhoneNumber, email } = useAuth();
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'messages' | 'whatsapp-calls' | 'phone-calls'>('messages');
  
  // Messages state
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string | null>(null);
  
  // WhatsApp Calls state
  const [whatsappCalls, setWhatsappCalls] = useState<Call[]>([]);
  const [whatsappCallsLoading, setWhatsappCallsLoading] = useState(false);
  const whatsappCallsLoadedRef = useRef(false);
  
  // Phone Calls state
  const [phoneCalls, setPhoneCalls] = useState<Call[]>([]);
  const [phoneCallsLoading, setPhoneCallsLoading] = useState(false);
  const [phoneNumberFromArray, setPhoneNumberFromArray] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: '',
    businessName: '',
    hasUnread: false,
    dateRange: 'all', // Changed from 'today' to 'all' to show all messages
    startDate: '',
    endDate: '',
  });
  
  // Sort state - default to sorting by timestamp (updatedAt for messages) descending (newest first)
  const [sort, setSort] = useState<SortState>({
    field: 'timestamp',
    direction: 'desc', // Always show newest first by default
  });

  // Filter toggle state
  const [filtersOpen, setFiltersOpen] = useState(false);

  // LiveKit calls are now handled in WhatsAppCallsTab component, not here

  // Removed duplicate WebSocket connection - using global one from App.tsx
  // The global WebSocket in App.tsx handles incoming calls and redirects to whatsapp-send-message page

  // Fetch phone numbers from the API
  const fetchPhoneNumbers = async () => {
    setLoading(true);
    setError(null);
    
    // Clear any existing phone numbers data first
    setPhoneNumbers([]);
    
    try {
      if (!displayPhoneNumber) {
        throw new Error('No display phone number found. Please log in again.');
      }

      // Use the display_phone_number to get the embedded signup data
      const embeddedData = await getEmbeddedSignupByPhone(displayPhoneNumber);
      logger.log('Phone Numbers List - Embedded data:', embeddedData);
      
      // Extract phone_numbers array for Phone Calls tab
      if (embeddedData?.records && embeddedData.records.length > 0) {
        const record = embeddedData.records[0];
        const phoneNumbers = record.phone_numbers || [];
        if (phoneNumbers.length > 0) {
          setPhoneNumberFromArray(phoneNumbers[0]);
        }
      }
      
      if (embeddedData?.records && Array.isArray(embeddedData.records)) {
        // Check if records array is empty
        if (embeddedData.records.length === 0) {
          logger.log('No WhatsApp Business accounts found for this user');
          setPhoneNumbers([]);
          return;
        }
        
        // Transform the records data to phone numbers format
        const transformedPhoneNumbers: PhoneNumber[] = embeddedData.records.map((record: any, index: number) => ({
          id: record.phoneNumberId || `phone_${index}`,
          phoneNumber: record.phoneNumber || displayPhoneNumber,
          phoneNumberId: record.phoneNumberId || `phone_${index}`,
          wabaId: record.wabaId || wabaId || 'default',
          businessName: record.businessName || `Business ${index + 1}`,
          status: record.status || 'active',
          lastMessageTime: record.last_onboarded_time || new Date().toISOString(),
          unreadCount: Math.floor(Math.random() * 10), // Random unread count 0-9
        }));

        logger.log('Transformed phone numbers:', transformedPhoneNumbers);
        logger.log('Setting phone numbers state with:', transformedPhoneNumbers.length, 'items');
        setPhoneNumbers(transformedPhoneNumbers);
      } else {
        // Fallback: create a single phone number entry from displayPhoneNumber
        const fallbackPhoneNumber: PhoneNumber = {
          id: 'default',
          phoneNumber: displayPhoneNumber,
          phoneNumberId: 'default',
          wabaId: wabaId || 'default',
          businessName: 'Your WhatsApp Business',
          status: 'active',
          lastMessageTime: new Date().toISOString(),
          unreadCount: 0,
        };
        logger.log('Using fallback phone number:', fallbackPhoneNumber);
        logger.log('Setting phone numbers state with fallback');
        setPhoneNumbers([fallbackPhoneNumber]);
      }
      
    } catch (err: any) {
      logger.error('Error fetching phone numbers:', err);
      setError(err?.message || 'Failed to load phone numbers');
    } finally {
      setLoading(false);
    }
  };

  // Load WhatsApp calls
  const loadWhatsAppCalls = useCallback(async (forceReload = false) => {
    // Prevent multiple simultaneous calls
    if (whatsappCallsLoading && !forceReload) {
      logger.log('WhatsApp calls already loading, skipping...');
      return;
    }
    
    // Prevent repeated calls if already loaded (unless forced)
    if (whatsappCallsLoadedRef.current && !forceReload) {
      logger.log('WhatsApp calls already loaded, skipping...');
      return;
    }
    
    logger.log('=== LOADING WHATSAPP CALLS ===');
    try {
      setWhatsappCallsLoading(true);
      whatsappCallsLoadedRef.current = true;
      // Use phoneNumberId from sessionStorage (logged-in user's phoneNumberId)
      const phoneNumberId = sessionStorage.getItem('phoneNumberId') || '';
      logger.log('Loading WhatsApp calls for phoneNumberId:', phoneNumberId);
      
      const result = await getWhatsAppCallRecords(phoneNumberId);
      logger.log('WhatsApp calls API result:', result);
      logger.log('WhatsApp calls API result structure:', {
        success: result.success,
        hasRecords: !!result.records,
        hasCalls: !!result.calls,
        hasData: !!result.data,
        recordsLength: result.records?.length,
        callsLength: result.calls?.length,
        dataLength: result.data?.length,
        firstRecord: result.records?.[0],
        firstCall: result.calls?.[0],
        firstData: result.data?.[0],
      });
      
      // API returns records array, not calls or data
      if (result.success || result.records || result.calls || result.data) {
        const callsData = result.records || result.calls || result.data || [];
        logger.log('=== RAW WHATSAPP CALLS DATA ===');
        logger.log('Total records:', callsData.length);
        logger.log('All records:', callsData);
        
        // Log each record's structure
        callsData.forEach((record: any, idx: number) => {
          logger.log(`Record ${idx} keys:`, Object.keys(record));
          logger.log(`Record ${idx} full data:`, record);
        });
        
        // Filter out message records - check if record has call-specific fields
        // If it has 'message' field but no call fields, it's likely a message record
        const actualCallRecords = callsData.filter((record: any) => {
          // Check if this looks like a call record (has call-specific fields)
          const hasCallFields = record.duration !== undefined || 
                               record.call_duration !== undefined ||
                               record.call_type !== undefined ||
                               record.call_status !== undefined ||
                               record.direction !== undefined ||
                               record.type === 'call' ||
                               record.messageType === 'call';
          
          // If it has a 'message' field but no call fields, it's probably a message
          const looksLikeMessage = record.message !== undefined && 
                                  record.messageType !== 'call' &&
                                  !hasCallFields;
          
          return !looksLikeMessage;
        });
        
        logger.log(`Filtered ${actualCallRecords.length} call records from ${callsData.length} total records`);
        
        // Map API data to Call interface
        const mappedCalls: Call[] = actualCallRecords.map((call: any, idx: number) => {
          // Log original call object to see what fields are available
          logger.log(`=== Mapping Call ${idx} ===`);
          logger.log('Original call object:', call);
          logger.log('Available fields:', Object.keys(call));
          logger.log('Field values:', {
            customerName: call.customerName,
            customer_name: call.customer_name,
            name: call.name,
            callerId: call.callerId,
            caller_id: call.caller_id,
            callerID: call.callerID,
            customerNumber: call.customerNumber,
            customer_number: call.customer_number,
            phoneNumber: call.phoneNumber,
            phone: call.phone,
            from: call.from,
            to: call.to,
            timestamp: call.timestamp,
            call_time: call.call_time,
            created_at: call.created_at,
            date: call.date,
            time: call.time,
            duration: call.duration,
            call_duration: call.call_duration,
            call_duration_seconds: call.call_duration_seconds,
            duration_seconds: call.duration_seconds,
            type: call.type,
            call_type: call.call_type,
            direction: call.direction,
            incoming: call.incoming,
            outgoing: call.outgoing,
            status: call.status,
            call_status: call.call_status,
            callStatus: call.callStatus,
          });
          
          // Handle various field name variations from API - check ALL possible variations
          // Priority: caller_name (from API) > customerName > customer_name > name
          const customerName = (call.caller_name || call.customerName || call.customer_name || call.name || '').trim();
          const callerId = (call.callerId || call.caller_id || call.callerID || '').trim();
          
          // Try multiple variations for customer number
          const customerNumber = call.customerNumber || 
                                call.customer_number || 
                                call.phoneNumber || 
                                call.phone || 
                                call.from || 
                                call.to || 
                                call.callerId ||
                                call.caller_id ||
                                '';
          
          // Try multiple variations for timestamp - don't use fallback unless absolutely necessary
          const timestamp = call.timestamp || 
                           call.call_time || 
                           call.created_at || 
                           call.date || 
                           call.time ||
                           call.start_time ||
                           call.end_time ||
                           call.callTimestamp ||
                           (call.createdAt ? new Date(call.createdAt).toISOString() : null) ||
                           null;
          
          // Try multiple variations for duration - check call_duration_seconds first (from API)
          const duration = call.call_duration_seconds !== undefined ? call.call_duration_seconds :
                          call.duration !== undefined ? call.duration :
                          call.call_duration !== undefined ? call.call_duration :
                          call.duration_seconds !== undefined ? call.duration_seconds :
                          call.callDuration !== undefined ? call.callDuration :
                          call.length !== undefined ? call.length :
                          null;
          
          // Determine call type - check multiple sources and handle incoming/outgoing correctly
          // Priority: call_direction (from API) > call_type > type > direction > incoming/outgoing flags
          let type = null;
          if (call.call_direction !== undefined && call.call_direction !== null) {
            // call_direction is the primary field from API (e.g., "inbound", "outbound")
            const direction = String(call.call_direction).toLowerCase();
            type = (direction === 'inbound' || direction === 'incoming' || direction === 'in') ? 'incoming' : 'outgoing';
          } else if (call.type !== undefined && call.type !== null) {
            type = String(call.type).toLowerCase();
          } else if (call.call_type !== undefined && call.call_type !== null) {
            type = String(call.call_type).toLowerCase();
          } else if (call.direction !== undefined && call.direction !== null) {
            const direction = String(call.direction).toLowerCase();
            type = (direction === 'inbound' || direction === 'incoming' || direction === 'in') ? 'incoming' : 'outgoing';
          } else if (call.incoming !== undefined && call.incoming !== null) {
            // Check boolean flag
            type = (call.incoming === true || call.incoming === 'true' || call.incoming === 1) ? 'incoming' : 'outgoing';
          } else if (call.outgoing !== undefined && call.outgoing !== null) {
            type = (call.outgoing === true || call.outgoing === 'true' || call.outgoing === 1) ? 'outgoing' : 'incoming';
          }
          
          // Normalize type - default to outgoing only if we couldn't determine
          const normalizedType: 'incoming' | 'outgoing' = type ? ((type === 'incoming' || type === 'in' || type === 'inbound') ? 'incoming' : 'outgoing') : 'outgoing';
          
          // Use current timestamp only if no timestamp found (should rarely happen)
          const finalTimestamp = timestamp || new Date().toISOString();
          
          // Parse duration properly
          let finalDuration: number | undefined = undefined;
          if (duration !== null && duration !== undefined) {
            if (typeof duration === 'number') {
              finalDuration = duration;
            } else if (typeof duration === 'string') {
              const parsed = parseInt(duration);
              finalDuration = isNaN(parsed) ? undefined : parsed;
            }
          }
          
          // Determine status based on duration: if duration < 60 seconds, show "missed"
          // Otherwise, use the actual status from API
          let normalizedStatus: 'completed' | 'missed' | 'failed' = 'completed';
          
          if (finalDuration !== undefined && finalDuration < 60) {
            // Call duration is less than 60 seconds, mark as missed
            normalizedStatus = 'missed';
          } else {
            // Use actual status from API
            const status = call.status || 
                          call.call_status || 
                          call.callStatus || 
                          call.callState ||
                          'completed';
            
            // Normalize status from API
            const statusLower = String(status).toLowerCase();
            if (statusLower === 'missed') {
              normalizedStatus = 'missed';
            } else if (statusLower === 'failed' || statusLower === 'error') {
              normalizedStatus = 'failed';
            } else {
              normalizedStatus = 'completed';
            }
          }
          
          // Get recording_url from API response
          const recordingUrl = call.recording_url || null;
          
          const mappedCall: Call = {
            customerName: customerName || undefined,
            callerId: callerId || undefined,
            customerNumber: customerNumber || 'Unknown',
            timestamp: finalTimestamp,
            duration: finalDuration,
            type: normalizedType,
            status: normalizedStatus,
            recording_url: recordingUrl,
          };
          
          logger.log(`Mapped call ${idx}:`, mappedCall);
          
          return mappedCall;
        });
        logger.log('Mapped WhatsApp calls:', mappedCalls);
        
        // Fetch audio for calls that don't have recording_url
        // Use Promise.all to fetch audio for all calls that need it
        const callsWithAudioPromises = mappedCalls.map(async (call) => {
          // If recording_url is null or empty, try to fetch audio
          const hasRecordingUrl = call.recording_url && typeof call.recording_url === 'string' && call.recording_url.trim() !== '';
          
          // Validate callerId - it should NOT be the same as phoneNumberId (business number)
          const cleanedCallerId = call.callerId ? cleanPhoneNumber(call.callerId) : '';
          const cleanedPhoneNumberId = cleanPhoneNumber(phoneNumberId);
          
          // Skip audio fetch if callerId is missing, empty, or matches business number
          if (!hasRecordingUrl && cleanedCallerId && cleanedCallerId !== cleanedPhoneNumberId) {
            try {
              logger.log('=== FETCHING AUDIO FOR CALL ===');
              logger.log('Caller ID:', cleanedCallerId);
              logger.log('Business Number:', cleanedPhoneNumberId);
              logger.log('API URL: GET /api/whatsapp/call-records/audio?business_number=' + cleanedPhoneNumberId + '&caller_id=' + cleanedCallerId);
              
              const audioUrl = await getCallAudio(phoneNumberId, cleanedCallerId);
              if (audioUrl) {
                logger.log('✅ Got audio URL from /audio API:', audioUrl);
                return { ...call, recording_url: audioUrl };
              } else {
                logger.log('❌ Audio API returned no URL for caller:', cleanedCallerId);
                return call;
              }
            } catch (audioError) {
              logger.error('❌ Error fetching audio for caller:', cleanedCallerId, audioError);
              return call;
            }
          } else if (call.recording_url) {
            logger.log('✅ Call already has recording_url:', call.callerId);
            return call;
          } else {
            if (cleanedCallerId === cleanedPhoneNumberId) {
              logger.log('⚠️ Skipping audio fetch - caller_id matches business_number (invalid):', cleanedCallerId);
            } else if (!cleanedCallerId) {
              logger.log('⚠️ Skipping audio fetch - no caller_id available');
            }
            return call;
          }
        });
        
        // Wait for all audio fetches to complete
        const callsWithAudio = await Promise.all(callsWithAudioPromises);
        
        setWhatsappCalls(callsWithAudio);
        logger.log('Loaded', callsWithAudio.length, 'WhatsApp calls with audio');
      } else {
        logger.log('API call failed or no success:', result);
        setWhatsappCalls([]);
      }
    } catch (err) {
      logger.error('Load WhatsApp calls error:', err);
      setWhatsappCalls([]);
      whatsappCallsLoadedRef.current = false; // Reset on error so it can retry
    } finally {
      setWhatsappCallsLoading(false);
    }
  }, [displayPhoneNumber, whatsappCallsLoading]);

  useEffect(() => {
    logger.log('PhoneNumbersList: useEffect triggered with displayPhoneNumber:', displayPhoneNumber);
    
    // Wait a bit for auth context to initialize on first load
    if (initialLoad && !displayPhoneNumber) {
      const timer = setTimeout(() => {
        logger.log('PhoneNumbersList: Initial load timeout - checking again');
        setInitialLoad(false);
      }, 500);
      return () => clearTimeout(timer);
    }
    
    if (!initialLoad || displayPhoneNumber) {
      setInitialLoad(false);
    }
    
    // Clear any existing data first
    setPhoneNumbers([]);
    setError(null);
    
    if (displayPhoneNumber) {
      logger.log('PhoneNumbersList: Calling fetchPhoneNumbers...');
      fetchPhoneNumbers();
      // Auto-load messages for this phone number
      loadMessagesOnLoad();
      // Auto-load WhatsApp calls (only if not already loaded)
      if (!whatsappCallsLoadedRef.current) {
        loadWhatsAppCalls();
      }
    } else if (!initialLoad) {
      logger.log('PhoneNumbersList: No displayPhoneNumber, setting loading to false');
      setLoading(false);
    }
  }, [displayPhoneNumber, initialLoad]);

  // Load WhatsApp calls when switching to WhatsApp calls tab
  useEffect(() => {
    if (activeTab === 'whatsapp-calls' && !whatsappCallsLoadedRef.current && !whatsappCallsLoading) {
      loadWhatsAppCalls();
    }
  }, [activeTab, loadWhatsAppCalls]);

  const loadMessagesOnLoad = async () => {
    logger.log('=== AUTO LOADING MESSAGES ON PAGE LOAD ===');
    try {
      const phoneNumberId = sessionStorage.getItem('phoneNumberId') || '';
      if (!phoneNumberId) {
        logger.log('No phoneNumberId in session, skipping messages load');
        return;
      }
      logger.log('Auto-loading messages for phoneNumberId:', phoneNumberId);

      const result = await getBusinessMessages(phoneNumberId);
      logger.log('Auto-load messages result:', result);
      
      if (result.success) {
        logger.log('Setting messages to:', result.messages);
        // Map API data to Message interface
        const mappedMessages: Message[] = (result.messages || []).map((msg: any) => ({
          customerName: msg.customerName,
          customerNumber: msg.customerNumber,
          timestamp: msg.timestamp, // Keep for sorting/filtering
          updatedAt: msg.updatedAt || msg.timestamp, // Use updatedAt for "Last Message" display, fallback to timestamp
          unreadCount: msg.unreadCount || 0, // Use unreadCount for "Unread" display
          message: msg.message || 'No message content',
          type: msg.type || 'text'
        }));
        logger.log('Mapped messages:', mappedMessages);
        setMessages(mappedMessages);
        setSelectedPhoneNumber(displayPhoneNumber || sessionStorage.getItem('display_phone_number') || '');
        logger.log('Auto-loaded', mappedMessages.length, 'messages');
      } else {
        logger.log('API call failed or no success:', result);
      }
    } catch (err) {
      logger.error('Auto-load messages error:', err);
    }
  };

  // Filter and sort based on active tab
  const getCurrentData = () => {
    if (activeTab === 'messages') return messages;
    if (activeTab === 'whatsapp-calls') return whatsappCalls;
    return phoneCalls;
  };

  const getCurrentLoading = () => {
    if (activeTab === 'messages') return messagesLoading;
    if (activeTab === 'whatsapp-calls') return whatsappCallsLoading;
    return phoneCallsLoading;
  };

  // Filter and sort messages
  const filteredAndSortedMessages = useMemo(() => {
    logger.log('Filtering messages:', { 
      totalMessages: messages.length, 
      filters, 
      sort,
      messages: messages 
    });
    
    let filtered = messages.filter(message => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          message.customerNumber.toLowerCase().includes(searchLower) ||
          (message.customerName || '').toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      
      // Date filter
      if (message.timestamp && filters.dateRange !== 'all') {
        const messageDate = new Date(message.timestamp);
        const now = new Date();
        
        if (filters.dateRange === 'today') {
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          if (messageDate < today) return false;
        } else if (filters.dateRange === 'yesterday') {
          const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          if (messageDate < yesterday || messageDate >= today) return false;
        } else if (filters.dateRange === 'last7days') {
          const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (messageDate < lastWeek) return false;
        } else if (filters.dateRange === 'last30days') {
          const lastMonth = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (messageDate < lastMonth) return false;
        } else if (filters.dateRange === 'custom' && filters.startDate && filters.endDate) {
          const startDate = new Date(filters.startDate);
          const endDate = new Date(filters.endDate);
          endDate.setHours(23, 59, 59, 999); // Include entire end date
          if (messageDate < startDate || messageDate > endDate) return false;
        }
      }
      
      return true;
    });

    // Sort - for messages, always use updatedAt; for other fields use the sort field
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      // For messages, always sort by updatedAt (or timestamp as fallback)
      if (sort.field === 'timestamp') {
        // Use updatedAt for messages, timestamp for calls
        aValue = (a as Message).updatedAt || a.timestamp;
        bValue = (b as Message).updatedAt || b.timestamp;
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      } else {
        aValue = a[sort.field];
        bValue = b[sort.field];
        
        // Handle string comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
      }
      
      if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });

    logger.log('Filtered messages result:', { 
      originalCount: messages.length, 
      filteredCount: filtered.length,
      filtered: filtered 
    });

    return filtered;
  }, [messages, filters, sort]);

  // Filter and sort WhatsApp calls
  const filteredAndSortedWhatsAppCalls = useMemo(() => {
    let filtered = whatsappCalls.filter(call => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          call.customerNumber.toLowerCase().includes(searchLower) ||
          (call.customerName || '').toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      
      // Date filter
      if (call.timestamp && filters.dateRange !== 'all') {
        const callDate = new Date(call.timestamp);
        const now = new Date();
        
        if (filters.dateRange === 'today') {
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          if (callDate < today) return false;
        } else if (filters.dateRange === 'yesterday') {
          const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          if (callDate < yesterday || callDate >= today) return false;
        } else if (filters.dateRange === 'last7days') {
          const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (callDate < lastWeek) return false;
        } else if (filters.dateRange === 'last30days') {
          const lastMonth = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (callDate < lastMonth) return false;
        } else if (filters.dateRange === 'custom' && filters.startDate && filters.endDate) {
          const startDate = new Date(filters.startDate);
          const endDate = new Date(filters.endDate);
          endDate.setHours(23, 59, 59, 999);
          if (callDate < startDate || callDate > endDate) return false;
        }
      }
      
      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = a[sort.field];
      let bValue: any = b[sort.field];
      
      if (sort.field === 'timestamp') {
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [whatsappCalls, filters, sort]);

  // Filter and sort Phone calls
  const filteredAndSortedPhoneCalls = useMemo(() => {
    let filtered = phoneCalls.filter(call => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          call.customerNumber.toLowerCase().includes(searchLower) ||
          (call.customerName || '').toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      
      // Date filter
      if (call.timestamp && filters.dateRange !== 'all') {
        const callDate = new Date(call.timestamp);
        const now = new Date();
        
        if (filters.dateRange === 'today') {
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          if (callDate < today) return false;
        } else if (filters.dateRange === 'yesterday') {
          const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          if (callDate < yesterday || callDate >= today) return false;
        } else if (filters.dateRange === 'last7days') {
          const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (callDate < lastWeek) return false;
        } else if (filters.dateRange === 'last30days') {
          const lastMonth = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (callDate < lastMonth) return false;
        } else if (filters.dateRange === 'custom' && filters.startDate && filters.endDate) {
          const startDate = new Date(filters.startDate);
          const endDate = new Date(filters.endDate);
          endDate.setHours(23, 59, 59, 999);
          if (callDate < startDate || callDate > endDate) return false;
        }
      }
      
      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = a[sort.field];
      let bValue: any = b[sort.field];
      
      if (sort.field === 'timestamp') {
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [phoneCalls, filters, sort]);

  // Get current filtered data based on active tab
  const getCurrentFilteredData = () => {
    if (activeTab === 'messages') return filteredAndSortedMessages;
    if (activeTab === 'whatsapp-calls') return filteredAndSortedWhatsAppCalls;
    return filteredAndSortedPhoneCalls;
  };

  // Debug logging
  logger.log('PhoneNumbersList: Component rendered with:', {
    wabaId,
    displayPhoneNumber,
    email,
    loading,
    error,
    phoneNumbersCount: phoneNumbers.length,
    phoneNumbers: phoneNumbers,
    messagesCount: messages.length,
    messages: messages,
    filteredAndSortedMessagesCount: filteredAndSortedMessages.length
  });

  // Pagination
  const currentFilteredData = getCurrentFilteredData();
  const totalItems = currentFilteredData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = currentFilteredData.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sort]);

  const handlePhoneNumberClick = async (phoneNumber: PhoneNumber) => {
    logger.log('=== PHONE NUMBER CLICKED ===');
    logger.log('Phone number object:', phoneNumber);
    logger.log('Phone number:', phoneNumber.phoneNumber, 'phoneNumberId:', phoneNumber.phoneNumberId);

    sessionStorage.setItem('phoneNumberId', phoneNumber.phoneNumberId);
    setSelectedPhoneNumber(phoneNumber.phoneNumber);
    setMessagesLoading(true);
    setMessages([]);

    try {
      logger.log('Loading messages for phoneNumberId:', phoneNumber.phoneNumberId);
      const result = await getBusinessMessages(phoneNumber.phoneNumberId);
      logger.log('=== API RESPONSE ===');
      logger.log('Messages API Result:', result);
      
      if (result.success) {
        logger.log('Setting messages:', result.messages);
        // Map API data to Message interface to ensure updatedAt and unreadCount are included
        const mappedMessages: Message[] = (result.messages || []).map((msg: any) => ({
          customerName: msg.customerName,
          customerNumber: msg.customerNumber,
          timestamp: msg.timestamp, // Keep for sorting/filtering
          updatedAt: msg.updatedAt || msg.timestamp, // Use updatedAt for "Last Message" display, fallback to timestamp
          unreadCount: msg.unreadCount || 0, // Use unreadCount for "Unread" display
          message: msg.message || 'No message content',
          type: msg.type || 'text'
        }));
        setMessages(mappedMessages);
      } else {
        logger.log('API returned success: false');
        setError('Failed to load messages');
      }
    } catch (err: any) {
      logger.error('=== API ERROR ===');
      logger.error('Error loading messages:', err);
      setError(err.message || 'Failed to load messages');
    } finally {
      logger.log('=== SETTING LOADING FALSE ===');
      setMessagesLoading(false);
    }
    
    // Also load WhatsApp calls when phone number is selected
    loadWhatsAppCalls();
  };

  const handleSort = (field: SortField) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleFilterChange = (key: keyof FilterState, value: string | boolean) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      businessName: '',
      hasUnread: false,
      dateRange: 'today',
      startDate: '',
      endDate: '',
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '--';
    // Use toLocaleString with user's locale and timezone
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getStatusBadge = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') return { label: 'Active', className: 'bg-green-100 text-green-700 border-green-200' };
    if (s === 'inactive') return { label: 'Inactive', className: 'bg-red-100 text-red-700 border-red-200' };
    if (s === 'pending') return { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
    return { label: status || 'Unknown', className: 'bg-gray-100 text-gray-700 border-gray-200' };
  };

  const getSortIcon = (field: SortField) => {
    if (sort.field !== field) return '↕️';
    return sort.direction === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return <LoadingComponent loading={true} message="Loading conversations..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      GAUTHAM - Phone Numbers List
      {/* Incoming Call Notification - Removed: Now handled globally in App.tsx with redirect */}
      {/* LiveKit Call Interface - Removed: Now handled in WhatsAppCallsTab component */}

      <div className="px-6 py-8">
        {/* Tabs Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('messages')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'messages'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <IoMdChatbubbles className="w-5 h-5" />
                <span>Customer Messages</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'messages'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {messages.length}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('whatsapp-calls')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'whatsapp-calls'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <IoMdCall className="w-5 h-5" />
                <span>WhatsApp Calls</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'whatsapp-calls'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {whatsappCalls.length}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('phone-calls')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'phone-calls'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <IoMdPhonePortrait className="w-5 h-5" />
                <span>Phone Calls</span>
                {phoneNumberFromArray && (
                  <span className="text-xs text-gray-500">
                    ({phoneNumberFromArray})
                  </span>
                )}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'phone-calls'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {phoneCalls.length}
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          {/* Filter Header - Always Visible */}
          <div 
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <div className="flex items-center space-x-3">
              <IoMdFunnel className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filters & Search</h2>
              <span className="text-sm text-gray-500">({totalItems} {activeTab === 'messages' ? 'customers' : 'calls'})</span>
            </div>
            <div className="flex items-center space-x-3">
              {(filters.search || filters.status || filters.hasUnread || filters.dateRange !== 'all') && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                  Filtered
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearFilters();
                }}
                className="flex items-center px-3 py-1 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
              >
                <IoMdRefresh className="w-4 h-4 mr-1" />
                Clear
              </button>
              <div className={`transform transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`}>
                <IoMdArrowDown className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Collapsible Filter Content */}
          {filtersOpen && (
            <div className="border-t border-gray-100 p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {/* Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                  <div className="relative">
                    <IoMdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder={activeTab === 'messages' ? 'Search conversations...' : 'Search calls...'}
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                    />
                  </div>
                </div>

                {/* Date Range Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <select
                    value={filters.dateRange}
                    onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last7days">Last 7 Days</option>
                    <option value="last30days">Last 30 Days</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                  >
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>

                {/* Unread Messages Filter - Only show for messages tab */}
                {activeTab === 'messages' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Unread Messages</label>
                    <div className="flex items-center space-x-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.hasUnread}
                          onChange={(e) => handleFilterChange('hasUnread', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <span className="ml-2 text-sm text-gray-700">Has unread</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Custom Date Range */}
              {filters.dateRange === 'custom' && (
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={filters.startDate}
                      onChange={(e) => handleFilterChange('startDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={filters.endDate}
                      onChange={(e) => handleFilterChange('endDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Results Summary */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <IoMdPeople className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium text-gray-700">
                        Showing {currentPageData.length} of {totalItems} {activeTab === 'messages' ? 'customers' : 'calls'}
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    Page {currentPage} of {totalPages}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content Table */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {activeTab === 'messages' && `Customer Messages (${messages.length})`}
              {activeTab === 'whatsapp-calls' && `WhatsApp Calls (${whatsappCalls.length})`}
              {activeTab === 'phone-calls' && `Phone Calls (${phoneCalls.length})`}
            </h2>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Showing {totalItems} {activeTab === 'messages' ? 'conversations' : 'calls'}
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Sort by:</span>
                <select
                  value={`${sort.field}-${sort.direction}`}
                  onChange={(e) => {
                    const [field, direction] = e.target.value.split('-');
                    setSort({ field: field as SortField, direction: direction as SortDirection });
                  }}
                  className="px-3 py-1 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="timestamp-desc">Newest First</option>
                  <option value="timestamp-asc">Oldest First</option>
                  <option value="customerName-asc">Name A-Z</option>
                  <option value="customerName-desc">Name Z-A</option>
                  <option value="customerNumber-asc">Phone A-Z</option>
                  <option value="customerNumber-desc">Phone Z-A</option>
                </select>
              </div>
            </div>
          </div>

          {getCurrentLoading() ? (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {activeTab === 'messages' ? 'Last Message' : 'Call Time'}
                      </th>
                      {activeTab === 'messages' && (
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unread</th>
                      )}
                      {(activeTab === 'whatsapp-calls' || activeTab === 'phone-calls') && (
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                      )}
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {[...Array(5)].map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                            <div className="h-4 bg-gray-200 rounded w-32"></div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-24"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-28"></div>
                        </td>
                        {activeTab === 'messages' ? (
                          <td className="px-6 py-4">
                            <div className="h-6 bg-gray-200 rounded-full w-8"></div>
                          </td>
                        ) : (
                          <td className="px-6 py-4">
                            <div className="h-4 bg-gray-200 rounded w-16"></div>
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <div className="h-8 bg-gray-200 rounded w-20"></div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : totalItems === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-6">
                {activeTab === 'messages' ? (
                  <IoMdChatbubbles className="w-10 h-10 text-blue-600" />
                ) : (
                  <IoMdCall className="w-10 h-10 text-blue-600" />
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {activeTab === 'messages' && 'No Messages Found'}
                {activeTab === 'whatsapp-calls' && 'No WhatsApp Calls Found'}
                {activeTab === 'phone-calls' && 'No Phone Calls Found'}
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                {activeTab === 'messages' && 'No messages found for this business number. Start a conversation to see messages here.'}
                {activeTab === 'whatsapp-calls' && 'No WhatsApp calls found for this business number.'}
                {activeTab === 'phone-calls' && 'No phone calls found for this business number.'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('customerName')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Customer</span>
                          {sort.field === 'customerName' && (
                            sort.direction === 'asc' ? <IoMdArrowUp className="w-4 h-4" /> : <IoMdArrowDown className="w-4 h-4" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('customerNumber')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Phone</span>
                          {sort.field === 'customerNumber' && (
                            sort.direction === 'asc' ? <IoMdArrowUp className="w-4 h-4" /> : <IoMdArrowDown className="w-4 h-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th 
                        className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('timestamp')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{activeTab === 'messages' ? 'Last Message' : 'Call Time'}</span>
                          {sort.field === 'timestamp' && (
                            sort.direction === 'asc' ? <IoMdArrowUp className="w-4 h-4" /> : <IoMdArrowDown className="w-4 h-4" />
                          )}
                        </div>
                      </th>
                      {activeTab === 'messages' && (
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unread</th>
                      )}
                      {(activeTab === 'whatsapp-calls' || activeTab === 'phone-calls') && (
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                      )}
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentPageData.map((item, index) => {
                      const isMessage = activeTab === 'messages';
                      const isWhatsAppCall = activeTab === 'whatsapp-calls';
                      const isPhoneCall = activeTab === 'phone-calls';
                      const customerName = isMessage ? (item as Message).customerName : (item as Call).customerName;
                      const callerId = !isMessage ? (item as Call).callerId : undefined;
                      const customerNumber = item.customerNumber;
                      // For messages: use updatedAt for display, for calls: use timestamp
                      const displayTimestamp = isMessage ? ((item as Message).updatedAt || item.timestamp) : item.timestamp;
                      const timestamp = item.timestamp; // Keep original for sorting
                      const unreadCount = isMessage ? ((item as Message).unreadCount || 0) : undefined;
                      const duration = !isMessage ? (item as Call).duration : undefined;
                      const callStatus = !isMessage ? (item as Call).status : undefined;
                      const callType = !isMessage ? (item as Call).type : undefined;
                      
                      // Use caller_id as fallback if name is not available
                      // Check for empty strings and null/undefined
                      const hasName = customerName && customerName.trim().length > 0;
                      const hasCallerId = callerId && callerId.trim().length > 0;
                      const displayName = hasName ? customerName : (hasCallerId ? callerId : 'Unknown Customer');
                      const displayInitial = (hasName ? customerName?.charAt(0) : (hasCallerId ? callerId?.charAt(0) : 'C')) || 'C';

                      return (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">
                                  {displayInitial}
                                </span>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {displayName}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {isMessage ? `Customer ID: ${index + 1}` : callType === 'incoming' ? 'Incoming' : 'Outgoing'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 font-mono">
                              {customerNumber}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {isMessage ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <IoMdCheckmarkCircle className="w-3 h-3 mr-1" />
                                Active
                              </span>
                            ) : (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                callStatus === 'completed' ? 'bg-green-100 text-green-800' :
                                callStatus === 'missed' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {callStatus === 'completed' && <IoMdCheckmarkCircle className="w-3 h-3 mr-1" />}
                                {callStatus === 'missed' && <IoMdCloseCircle className="w-3 h-3 mr-1" />}
                                {callStatus === 'failed' && <IoMdClock className="w-3 h-3 mr-1" />}
                                {callStatus || 'Unknown'}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {new Date(displayTimestamp).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(displayTimestamp).toLocaleTimeString()}
                            </div>
                          </td>
                          {isMessage ? (
                            <td className="px-6 py-4">
                              {unreadCount !== undefined && unreadCount > 0 ? (
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white text-xs font-medium">
                                  {unreadCount}
                                </span>
                              ) : null}
                            </td>
                          ) : (
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {duration ? `${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, '0')}` : '--'}
                              </div>
                            </td>
                          )}
                          <td className="px-6 py-4">
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                // Clean phone number (remove non-digits)
                                const phoneNumberClean = customerNumber.replace(/[^\d]/g, '');
                                
                                // Call updateCount API before navigating
                                try {
                                  const phoneNumberId = sessionStorage.getItem('phoneNumberId') || '';
                                  if (phoneNumberId && phoneNumberClean) {
                                    await updateCount(phoneNumberClean, phoneNumberId);
                                    logger.log('updateCount API called successfully');
                                  }
                                } catch (error) {
                                  logger.error('Error calling updateCount:', error);
                                  // Continue navigation even if API call fails
                                }
                                
                                // Store customer info in sessionStorage
                                sessionStorage.setItem('selectedCustomer', JSON.stringify({
                                  phone: customerNumber,
                                  name: customerName
                                }));
                                
                                if (isMessage) {
                                  logger.log('View Details clicked for message:', item);
                                  logger.log('Navigating to:', `/events/whatsapp-send-message/${phoneNumberClean}/messages`);
                                  // Navigate to messages tab with phone number
                                  navigate(`/events/whatsapp-send-message/${phoneNumberClean}/messages`);
                                } else if (isWhatsAppCall) {
                                  logger.log('View Details clicked for WhatsApp call:', item);
                                  logger.log('Navigating to:', `/events/whatsapp-send-message/${phoneNumberClean}/whatsappcalls`);
                                  // Navigate to WhatsApp calls tab with phone number
                                  navigate(`/events/whatsapp-send-message/${phoneNumberClean}/whatsappcalls`);
                                } else if (isPhoneCall) {
                                  logger.log('View Details clicked for phone call:', item);
                                  logger.log('Navigating to:', `/events/whatsapp-send-message/${phoneNumberClean}/phonecalls`);
                                  // Navigate to phone calls tab with phone number
                                  navigate(`/events/whatsapp-send-message/${phoneNumberClean}/phonecalls`);
                                }
                              }}
                              className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium text-sm shadow-sm hover:shadow-md"
                            >
                              <IoMdEye className="w-4 h-4 mr-1" />
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">Show:</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-600">per page</span>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors font-medium"
                >
                  First
                </button>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors font-medium"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 text-sm border rounded-lg font-medium transition-all duration-200 ${
                          currentPage === pageNum
                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-500 shadow-lg'
                            : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors font-medium"
                >
                  Next
                </button>
                
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors font-medium"
                >
                  Last
                </button>
              </div>

              <div className="text-sm text-gray-600 font-medium">
                {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
